// ./react-app/src/components/DailyControllingChart.tsx
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ResponsiveLine, Serie } from '@nivo/line';
import { Box, useTheme } from '@mui/material';
import { tokens } from '../theme';
import fetchData from '../helpers/fetchData';
import { useChartTheme } from '../themes/chartTheme';
import { FilterContext } from '../contexts/FilterContext';
import { dateToSqlDate } from '../helpers/dateFuncs';
import { determineAggregation } from '../helpers/chartUtils';
import LoadingSpinner from './LoadingSpinner';

export interface DailyControllingData {
  customer: string;
  client_id: number;
  client_name: string;
  date: string;
  count: number;
}

interface DailyControllingChartProps {
  selectedCustomers: string[];
  selectedClients: number[];
}

const customerMapping: Record<string, { name: string; color: string }> = {
  ft: { name: 'Finanztrends', color: '#2563eb' },
  bg: { name: 'Börse Global', color: '#10b981' },
  sw: { name: 'Stock-World', color: '#f59e0b' },
  tt: { name: 'Trading-Treff', color: '#ef4444' },
  be: { name: 'Börse-Express', color: '#8b5cf6' },
  foreign: { name: 'Foreign Leads', color: '#6b7280' },
};

const getISOWeek = (date: Date): number => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

const formatDateForAggregation = (dateStr: string, aggregation: string): string => {
  const date = new Date(dateStr);

  switch (aggregation) {
    case 'monthly':
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'short',
      });
    case 'weekly':
      const weekStart = new Date(date);
      const dayOfWeek = date.getDay() || 7;
      weekStart.setDate(date.getDate() - dayOfWeek + 1);
      return `KW ${getISOWeek(weekStart)}, ${weekStart.toLocaleDateString('de-DE', { month: 'short' })}`;
    case 'daily':
    default:
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
      });
  }
};

const aggregateData = (
  data: DailyControllingData[],
  aggregation: string,
): DailyControllingData[] => {
  const aggregatedMap = new Map<string, DailyControllingData>();

  data.forEach((item) => {
    const aggregatedDate = formatDateForAggregation(item.date, aggregation);
    const key = `${item.customer}-${item.client_id}-${aggregatedDate}`;

    if (!aggregatedMap.has(key)) {
      aggregatedMap.set(key, {
        customer: item.customer,
        client_id: item.client_id,
        client_name: item.client_name,
        date: aggregatedDate,
        count: 0,
      });
    }

    const existing = aggregatedMap.get(key)!;
    existing.count += item.count;
  });

  return Array.from(aggregatedMap.values());
};

const processDataToSeries = (
  data: DailyControllingData[],
  selectedClients: number[],
  selectedCustomers: string[],
): Serie[] => {
  // Aggregate data by customer, summing selected clients
  const customerData = new Map<string, Map<string, number>>();

  // Find all unique dates in the dataset
  const allDates = new Set<string>();
  data.forEach((item) => {
    if (selectedClients.includes(item.client_id) && selectedCustomers.includes(item.customer)) {
      allDates.add(item.date);
    }
  });

  // Sort dates chronologically
  const sortedDates = Array.from(allDates).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime(),
  );

  // Initialize maps for each customer
  selectedCustomers.forEach((customer) => {
    customerData.set(customer, new Map());
  });

  // Aggregate data by customer and date, summing across selected clients
  data.forEach((item) => {
    if (selectedClients.includes(item.client_id) && selectedCustomers.includes(item.customer)) {
      const customerMap = customerData.get(item.customer);
      if (customerMap) {
        const existing = customerMap.get(item.date) || 0;
        customerMap.set(item.date, existing + item.count);
      }
    }
  });

  // Convert to series format, filling missing dates with 0
  return selectedCustomers.map((customer) => {
    const customerMap = customerData.get(customer) || new Map();

    const dataPoints = sortedDates.map((date) => ({
      x: date,
      y: customerMap.get(date) || 0, // Fill missing dates with 0
    }));

    return {
      id: customerMapping[customer]?.name || customer,
      color: customerMapping[customer]?.color || '#000000',
      data: dataPoints,
    };
  });
};

const DailyControllingChart: React.FC<DailyControllingChartProps> = ({
  selectedCustomers,
  selectedClients,
}) => {
  const chartTheme = useChartTheme();
  const { filterState } = useContext(FilterContext)!;

  const [data, setData] = useState<DailyControllingData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Determine aggregation based on date range
  const aggregation = useMemo(() => {
    if (!filterState.startDate || !filterState.endDate) return 'daily';
    return determineAggregation(filterState.startDate, filterState.endDate);
  }, [filterState.startDate, filterState.endDate]);

  useEffect(() => {
    const fetchChartData = async () => {
      if (
        !filterState.startDate ||
        !filterState.endDate ||
        selectedCustomers.length === 0 ||
        selectedClients.length === 0
      ) {
        setData([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const options = {
          start_date: dateToSqlDate(filterState.startDate),
          end_date: dateToSqlDate(filterState.endDate),
          customers: selectedCustomers.join(','),
          clients: selectedClients.join(','),
        };

        const rawData = (await fetchData<DailyControllingData[]>(
          '/api/daily-controlling/multi-customer-client-data',
          options,
        )) as DailyControllingData[];

        // Apply aggregation if needed
        const processedData =
          aggregation !== 'daily' ? aggregateData(rawData, aggregation) : rawData;

        setData(processedData);
      } catch (err: any) {
        console.error('Error fetching daily controlling data:', err);
        setError(err.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [filterState.startDate, filterState.endDate, selectedCustomers, selectedClients, aggregation]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Box p={2} color="error.main">
        Error loading chart: {error}
      </Box>
    );
  }

  if (selectedCustomers.length === 0 || selectedClients.length === 0) {
    return (
      <Box p={2} color="text.secondary" textAlign="center">
        Wählen Sie mindestens einen Kunden und Client aus, um Daten anzuzeigen.
      </Box>
    );
  }

  const series = processDataToSeries(data, selectedClients, selectedCustomers);

  if (series.every((s) => s.data.length === 0)) {
    return (
      <Box p={2} color="text.secondary" textAlign="center">
        Keine Daten für den ausgewählten Zeitraum verfügbar.
      </Box>
    );
  }

  return (
    <ResponsiveLine
      data={series}
      theme={chartTheme}
      margin={{ top: 20, right: 130, bottom: 60, left: 80 }}
      xScale={{ type: 'point' }}
      yScale={{
        type: 'linear',
        min: 'auto',
        max: 'auto',
        stacked: false,
        reverse: false,
      }}
      axisTop={null}
      axisRight={null}
      axisBottom={{
        tickSize: 5,
        tickPadding: 10,
        tickRotation: -30,
        legend: 'Datum',
        legendOffset: 45,
        legendPosition: 'middle',
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 10,
        tickRotation: 0,
        legend: 'Leads',
        legendOffset: -60,
        legendPosition: 'middle',
      }}
      pointSize={6}
      pointColor={{ theme: 'background' }}
      pointBorderWidth={2}
      pointBorderColor={{ from: 'serieColor' }}
      pointLabelYOffset={-12}
      useMesh={true}
      lineWidth={3}
      enableGridX={true}
      enableGridY={true}
      colors={(serie) => serie.color}
      tooltip={({ point }) => (
        <Box
          sx={{
            padding: '8px 12px',
            color: '#222',
            background: '#fff',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <strong>{point.serieId}</strong>
          <br />
          {point.data.xFormatted}: {point.data.yFormatted} Leads
        </Box>
      )}
      legends={[
        {
          anchor: 'bottom-right',
          direction: 'column',
          justify: false,
          translateX: 120,
          translateY: 0,
          itemsSpacing: 2,
          itemDirection: 'left-to-right',
          itemWidth: 100,
          itemHeight: 18,
          itemOpacity: 0.85,
          symbolSize: 12,
          symbolShape: 'circle',
          symbolBorderColor: 'rgba(0, 0, 0, .5)',
          effects: [
            {
              on: 'hover',
              style: {
                itemBackground: 'rgba(0, 0, 0, .03)',
                itemOpacity: 1,
              },
            },
          ],
        },
      ]}
    />
  );
};

export default DailyControllingChart;
